import { useEffect, useState, useRef } from "react";
import QRCode from "react-qr-code";
import { useConfig } from "../../context/configContext.js";
import "../../App.css";
import toast from "react-hot-toast";

const ChargerQRGenerator = () => {
  const { config } = useConfig();

  const [inputs, setInputs] = useState({});
  const [qrValues, setQrValues] = useState({});
  const [dayModePreview, setDayModePreview] = useState(null);
  const [dayModeFile, setDayModeFile] = useState(null);
  const [nightModePreview, setNightModePreview] = useState(null);
  const [nightModeFile, setNightModeFile] = useState(null);
  const [cpoName, setCpoName] = useState(config.cpo_name || "");
  const refs = useRef({});

  useEffect(() => {
    setCpoName(config.cpo_name || "");
  }, [config.cpo_name, config.station_id]);

  // ------------------ Handlers ------------------
  const handleChange = (key, value) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const gunFileMap = {
    GUN1: "GunA",
    GUN2: "GunB",
    GUN3: "GunC",
    GUN4: "GunD",
    GUN5: "GunE",
    GUN6: "GunF",
    GUN7: "GunG",
    GUN8: "GunH",
    GUN9: "GunI",
    GUN10: "GunJ",
    GUN11: "GunK",
    GUN12: "GunL",
  };

  const generateQR = (key) => {
    if (!inputs[key]) return; // prevent empty QR
    setQrValues((prev) => ({ ...prev, [key]: inputs[key] }));
  };

  const handleLogoUpload = (e, mode) => {
    const file = e.target.files[0];
    if (!file) return;

    // Convert to Base64
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      const base64String = reader.result; // full base64 string
      const preview = URL.createObjectURL(file);

      if (mode === "day") {
        setDayModePreview(preview);
        setDayModeFile(base64String); // store base64
      } else if (mode === "night") {
        setNightModePreview(preview);
        setNightModeFile(base64String); // store base64
      }
    };

    reader.onerror = (error) => {
      console.error("Error converting to base64:", error);
    };
  };

  const getQrBase64 = (key) => {
    const svg = refs.current[key]?.querySelector("svg");
    if (!svg) return null;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const encoded = btoa(unescape(encodeURIComponent(svgString)));

    return `data:image/svg+xml;base64,${encoded}`;
  };

  const handleSubmit = async () => {
    const result = [];

    config.dispensersData?.forEach((disp) => {
      disp.guns?.forEach((gun) => {
        const key = gun.gun_id;

        if (qrValues[key]) {
          const qrImage = getQrBase64(key);

          result.push({
            file_name: `${gunFileMap[gun.gun_id]}.jpeg`,
            QrValue: qrValues[key],
            qr_image: qrImage,
          });
        }
      });
    });

    const payload = {
      qrData: result,
      dayLogo: dayModeFile,
      nightLogo: nightModeFile,
      station_id: config.station_id,
      cpo_name: cpoName.trim(),
    };

    if (result.length === 0 && !dayModeFile && !nightModeFile && !cpoName.trim()) {
      toast.error("Please generate at least one QR or enter a CPO name");
      return;
    }

    const toastId = toast.loading("Sending QR...");

    try {
      const res = await fetch("/api/send-qr", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("QR and CPO details sent successfully", { id: toastId });
      } else {
        toast.error(data.message || "Failed to send QR", { id: toastId });
      }
    } catch (err) {
      console.error(err);
      toast.error("Network error", { id: toastId });
    }
  };

  // ------------------ UI ------------------
  return (
    <div className="w-full max-w-full space-y-6 overflow-x-hidden px-4 pt-6 text-slate-50 sm:px-6 lg:px-8">
      {/* -------- Header -------- */}
      <div className="flex min-w-0 max-w-full flex-col gap-4 rounded-lg border border-cyan-200/20 bg-white/95 p-5 text-slate-900 shadow-2xl shadow-blue-950/20 sm:flex-row sm:items-center sm:justify-between">
        {/* Left side (logos + text) */}
        <div className="flex flex-col gap-4">
          {/* Logo Uploads */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Day Mode Logo */}
            <div className="flex flex-col items-start gap-3">
              <label className="text-sm font-semibold text-slate-700">
                Day Mode Logo
              </label>
              <label
                className="
                cursor-pointer
                border-2 border-dashed border-cyan-300
                rounded-lg
                p-4
                w-48 h-28
                flex flex-col items-center justify-center
                bg-sky-50/80
                text-sky-700
                hover:border-cyan-500 hover:text-blue-700
                transition
              "
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleLogoUpload(e, "day")}
                  className="hidden"
                />

                {!dayModePreview ? (
                  <>
                    <span className="text-sm font-medium">Upload Logo</span>
                    <span className="text-xs">Click or drag image</span>
                  </>
                ) : (
                  <img
                    src={dayModePreview}
                    alt="Day Mode Logo"
                    className="h-full object-contain"
                  />
                )}
              </label>

              {/* Actions */}
              {dayModePreview && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setDayModePreview(null);
                      setDayModeFile(null);
                    }}
                    className="
                      px-3 py-1 text-sm bg-red-500 text-white rounded
                      hover:bg-red-600 transition
                    "
                  >
                    Remove
                  </button>

                  <label className="cursor-pointer rounded bg-gradient-to-r from-cyan-400 to-blue-600 px-3 py-1 text-sm font-semibold text-slate-950 hover:brightness-110">
                    Replace
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleLogoUpload(e, "day")}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Night Mode Logo */}
            <div className="flex flex-col items-start gap-3">
              <label className="text-sm font-semibold text-slate-700">
                Night Mode Logo
              </label>
              <label
                className="
                cursor-pointer
                border-2 border-dashed border-blue-300
                rounded-lg
                p-4
                w-48 h-28
                flex flex-col items-center justify-center
                bg-sky-50/80
                text-sky-700
                hover:border-blue-600 hover:text-blue-700
                transition
              "
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleLogoUpload(e, "night")}
                  className="hidden"
                />

                {!nightModePreview ? (
                  <>
                    <span className="text-sm font-medium">Upload Logo</span>
                    <span className="text-xs">Click or drag image</span>
                  </>
                ) : (
                  <img
                    src={nightModePreview}
                    alt="Night Mode Logo"
                    className="h-full object-contain"
                  />
                )}
              </label>

              {/* Actions */}
              {nightModePreview && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setNightModePreview(null);
                      setNightModeFile(null);
                    }}
                    className="
                      px-3 py-1 text-sm bg-red-500 text-white rounded
                      hover:bg-red-600 transition
                    "
                  >
                    Remove
                  </button>

                  <label className="cursor-pointer rounded bg-gradient-to-r from-cyan-400 to-blue-600 px-3 py-1 text-sm font-semibold text-slate-950 hover:brightness-110">
                    Replace
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleLogoUpload(e, "night")}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          <div>
            <h1 className="text-lg font-semibold text-slate-900 sm:text-xl">
              StationId: {config.station_id}
            </h1>
            <h2 className="text-sm text-slate-600 sm:text-base">
              Station Name: {config.station_name}
            </h2>
            <label className="mt-4 block text-sm font-semibold text-slate-700">
              CPO Name
            </label>
            <input
              value={cpoName}
              onChange={(e) => setCpoName(e.target.value)}
              placeholder="Enter CPO name"
              className="mt-2 min-h-10 w-full max-w-md rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-cyan-400"
            />
          </div>
        </div>

        {/* Right side (Submit button) */}
        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            className="
            px-5 py-2 bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-950 font-semibold rounded
            hover:brightness-110 transition
            w-full sm:w-auto
          "
          >
            Submit
          </button>
        </div>
      </div>

      {/* -------- Dispensers -------- */}
      {config.dispensersData
        ?.slice()
        .sort((a, b) => a.dispensor_id.localeCompare(b.dispensor_id))
        .map((disp, dIndex) => (
          <div key={dIndex} className="space-y-4">
            {/* Disp Header */}
            <h2 className="text-base font-bold text-cyan-50 sm:text-lg">
              Dispenser: {disp.dispensor_id} ({disp.name})
            </h2>

            {/* Guns Grid */}
            <div className="grid min-w-0 max-w-full grid-cols-1 items-start gap-6 md:grid-cols-2 lg:grid-cols-3">
              {disp.guns?.map((gun, gIndex) => {
                const key = `${gun.gun_id}`;

                return (
                  <div
                    key={key}
                    className="
                    border border-cyan-100/80 rounded-lg p-4
                    bg-white/95 text-slate-900
                    flex flex-col 
                    items-center sm:items-start
                    text-center sm:text-left
                    w-full h-full
                    shadow-sm shadow-blue-950/10 hover:shadow-md transition
                  "
                  >
                    {/* Gun Title */}
                    <h3 className="mb-2 text-base font-semibold text-slate-900 sm:text-lg">
                      {gun.gun_id}
                    </h3>

                    {/* Input */}
                    <input
                      value={inputs[key] || ""}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={`Enter QR string`}
                      className="
                      w-full p-2 border border-sky-200 bg-sky-50 text-slate-900 rounded mb-3
                      text-sm sm:text-base
                      focus:outline-none focus:ring-2 focus:ring-cyan-400
                    "
                    />

                    {/* Button */}
                    <button
                      onClick={() => generateQR(key)}
                      className="
                      px-4 py-2 bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-950 font-semibold rounded
                      w-full sm:w-auto
                      hover:brightness-110 transition
                    "
                    >
                      Generate
                    </button>

                    {/* QR Code */}
                    {qrValues[key] && (
                      <div className="mt-4 flex justify-center w-full">
                        <div
                          ref={(el) => {
                            if (el) refs.current[key] = el;
                          }}
                          className="bg-white p-3 rounded"
                        >
                          <QRCode
                            value={qrValues[key]}
                            size={140}
                            className="
                            sm:w-[180px] sm:h-[180px]
                            lg:w-[200px] lg:h-[200px]
                          "
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
    </div>
  );
};

export default ChargerQRGenerator;
